import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import { NoteIcon } from '@sharedCR/coreDesignSystem/icons';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { openDialogAction } from 'store/global/actions';
import { cancelSendingBundleAction } from 'store/loanApplications/actions';

import { signerTableWrapperStyles, tableCellStyles } from '../../DisclosureDelivery.styles';
import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';
import useDisclosureDelivery from '../../hooks/useDisclosureDelivery';
import CancelSendingBundlePopup from '../CancelSendingBundlePopup';

import { getScheduledTableConfig } from './ScheduledDeliveryTable.config';

const ScheduledDeliveryTable = ({
  setIsLoading,
  generatedDate = '',
  formBundleId,
  isCanceled = false,
}) => {
  const dispatch = useDispatch();

  const { loanId, isCancelSendingAllowed, refresh } = useDisclosureDelivery();

  // This table was written for a still-pending bundle, so its status was a literal. A bundle
  // cancelled before its scheduled generation now reaches it too, and must not claim to be pending.
  const scheduledBundleData = useMemo(
    () => [{ status: isCanceled ? 'Canceled' : 'Pending', generatedDate }],
    [generatedDate, isCanceled],
  );

  const cancelSendingBundle = useCallback(() => {
    setIsLoading(true);

    return dispatch(cancelSendingBundleAction({ loanId, formBundleId }))
      .then(() => refresh())
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [setIsLoading, dispatch, loanId, formBundleId, refresh]);

  const showCancelSendingBundlePopup = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Confirm bundle cancellation',
        body: () => (
          <CancelSendingBundlePopup date={generatedDate} onConfirm={cancelSendingBundle} />
        ),
      }),
    );
  }, [cancelSendingBundle, dispatch, generatedDate]);

  const tableConfig = useMemo(
    () =>
      getScheduledTableConfig({
        onCancelButtonClick: showCancelSendingBundlePopup,
        // Cancelling an already-cancelled bundle is a state-invalid mutation, so the control is
        // withheld rather than left to fail server-side.
        enableCancel: isCancelSendingAllowed && !isCanceled,
      }),
    [showCancelSendingBundlePopup, isCancelSendingAllowed, isCanceled],
  );

  return (
    <Box sx={signerTableWrapperStyles}>
      <Button
        disabled
        data-testid={DisclosureDeliveryTestIds.VIEW_DOCUMENTS_BUTTON}
        startIcon={<NoteIcon />}
      >
        View Documents
      </Button>

      <TableCDS config={tableConfig} customStyles={tableCellStyles} data={scheduledBundleData} />
    </Box>
  );
};

ScheduledDeliveryTable.propTypes = {
  ...WithLoadingPropTypes,
  formBundleId: PropTypes.string.isRequired,
  generatedDate: PropTypes.string,
  isCanceled: PropTypes.bool,
};

export default withLoading(ScheduledDeliveryTable);
